/**
 * Classify a (fromAccount.type, toAccount.type) pair into a transfer
 * subtype. The four legal subtypes mirror `prototype/cash/transfers.html`'s
 * `.subtype-pill` variants:
 *
 *   bank → bank   → BANK_TO_BANK
 *   bank → cash   → BANK_TO_CASH   (petty-cash top-up / replenishment)
 *   cash → bank   → CASH_TO_BANK   (banking surplus cash)
 *   bank → card   → BANK_TO_CARD   (credit-card payment)
 *
 * Anything else (e.g. `card → bank`, `card → card`, same-account legs)
 * is invalid per ADR-0012 §10.7 — cards cannot be the source of a
 * transfer. Callers receive `null` and must surface the constraint.
 *
 * @see prototype/cash/transfers.html (header comment)
 * @see docs/adr/0012-bank-accounts-and-cash-movements.md §10.7
 */
export type BankAccountType = "bank" | "cash" | "credit_card";

export type TransferSubtype = "bank_to_bank" | "bank_to_cash" | "cash_to_bank" | "bank_to_card";

export function classifySubtype(
  fromType: BankAccountType,
  toType: BankAccountType,
): TransferSubtype | null {
  if (fromType === "bank" && toType === "bank") return "bank_to_bank";
  if (fromType === "bank" && toType === "cash") return "bank_to_cash";
  if (fromType === "cash" && toType === "bank") return "cash_to_bank";
  if (fromType === "bank" && toType === "credit_card") return "bank_to_card";
  return null;
}

/** Wire-friendly label for the subtype pill — matches the prototype's chip text. */
export const SUBTYPE_LABEL: Record<TransferSubtype, string> = {
  bank_to_bank: "BANK→BANK",
  bank_to_cash: "BANK→CASH",
  cash_to_bank: "CASH→BANK",
  bank_to_card: "BANK→CARD",
};
